import { type Ancho, anchoDe as anchoDeDatos, type NivelDeLugar, type ValorDeLugar } from '@mc/campos'

/**
 * El catálogo de campos de formulario (CLAUDE.md §9). Vive en código, se amplía
 * por despliegue y queda tipado para el tema: **veinte campos que funcionan bien
 * antes que sesenta y cinco a medias**.
 *
 * Aquí no hay nada de aspecto. Un campo declara qué se pregunta, qué se admite
 * como respuesta y qué hace falta para guardarla; **cómo se ve lo pone el tema**.
 */
export const TIPOS_DE_CAMPO = [
  'texto',
  'area',
  'email',
  'telefono',
  'numero',
  'fecha',
  'hora',
  'desplegable',
  'opcion-multiple',
  'casillas',
  'archivo',
  'direccion',
  'lugar',
  'url',
  'valoracion',
  'escala',
  'consentimiento',
  'oculto',
  'informativo',
  'paso',
  'repetidor',
  'calculado',
  'pago',
] as const

export type TipoDeCampo = (typeof TIPOS_DE_CAMPO)[number]

/** Los que no recogen una respuesta: no se validan ni se guardan como valor. */
export const SIN_VALOR: TipoDeCampo[] = ['informativo', 'paso']

/**
 * Los que el visitante no rellena: los pone el sistema (§9). El `pago` también: el
 * importe lo calcula el servidor y lo cobra la pasarela del cliente (§23, fase 14);
 * lo que mande el navegador no se cree.
 */
export const DEL_SISTEMA: TipoDeCampo[] = ['oculto', 'calculado', 'pago']

export type Opcion = { etiqueta: string; valor: string }

/**
 * **Cuánto ocupa un campo en la fila** (las «columnas» de un constructor de formularios, sin
 * contenedores): entero, la mitad, un tercio o dos tercios. Los campos seguidos que caben
 * comparten fila; en una pantalla estrecha todos van enteros. Es diseño, pero lo decide quien
 * monta el formulario, como el orden: el tema solo lo pinta. Los valores son nombres (§28):
 * de un `select` sale un enum de Postgres y otro de GraphQL.
 */
// Los anchos, los niveles de lugar y la forma de un lugar son los mismos que los de los campos
// de datos (`@mc/campos`, plan del CMS dinámico §5.1): se reexportan de allí, con la misma API.
export { ANCHOS, COLUMNAS_DE_ANCHO, NIVELES_DE_LUGAR } from '@mc/campos'
export type { Ancho, NivelDeLugar, ValorDeLugar } from '@mc/campos'

/** Los que no tienen ancho: el oculto no se ve, y un salto de paso parte el formulario de lado a lado. */
export const SIN_ANCHO: TipoDeCampo[] = ['oculto', 'paso']

/** El ancho de un campo: el suyo, o entero si no lo dice (o si es de los que van siempre enteros). */
export const anchoDe = (campo: { tipo: string; ancho?: null | string }): Ancho => anchoDeDatos(campo, SIN_ANCHO)

/**
 * El nivel que se pide en un país con `niveles` divisiones: «el segundo» en un país que solo
 * tiene una es esa; «el último», la más honda que tenga. 0 si el país no tiene divisiones:
 * entonces la respuesta es solo el país.
 */
export const nivelPedido = (nivel: null | string | undefined, niveles: number): number => {
  const n = Math.max(0, Math.floor(niveles))
  if (nivel === 'primero') return Math.min(1, n)
  if (nivel === 'segundo') return Math.min(2, n)
  return n
}

/**
 * Lo que se guarda de un lugar: el país (ISO 3166-1), el código oficial del lugar elegido y el
 * camino entero en texto («Perú › Lima › Lima › Miraflores»), que **rehace el servidor** con
 * los nombres oficiales. Sin JavaScript el lugar se escribe a mano: entonces `codigo` es
 * `null` y `texto` lleva lo que escribió el visitante.
 */
export const esValorDeLugar = (valor: unknown): valor is Partial<ValorDeLugar> =>
  Boolean(valor) && typeof valor === 'object' && !Array.isArray(valor) && ('pais' in (valor as object) || 'codigo' in (valor as object) || 'texto' in (valor as object))

/** El rango de una escala, saneado: empieza en 0 o en 1, y acaba entre 3 y 10. */
export const rangoDeEscala = (campo: { minimo?: null | number; maximo?: null | number }): { minimo: number; maximo: number } => {
  const minimo = Number(campo.minimo) === 1 ? 1 : 0
  const maximo = campo.maximo === null || campo.maximo === undefined || !Number.isFinite(Number(campo.maximo)) ? 10 : Math.min(10, Math.max(3, Math.round(Number(campo.maximo))))
  return { minimo, maximo }
}

export type Regla = { campo: string; operador: 'contiene' | 'distinto' | 'igual' | 'mayor' | 'menor' | 'rellenado' | 'vacio'; valor?: null | string }

export type Condicion = { modo: 'alguna' | 'todas'; reglas: Regla[] }

export type Campo = {
  tipo: TipoDeCampo
  /** La clave con la que se guarda la respuesta. Única en el formulario. */
  clave: string
  etiqueta: string
  ayuda?: null | string
  marcador?: null | string
  obligatorio?: boolean | null
  /** Cuánto ocupa en la fila (`anchoDe`). Entero si no lo dice. */
  ancho?: Ancho | null
  /** Se enseña solo si se cumple (§9). Un campo escondido no se valida ni se guarda. */
  condicion?: Condicion | null
  opciones?: null | Opcion[]
  /** texto, área: largo admitido. */
  minimoDeCaracteres?: null | number
  maximoDeCaracteres?: null | number
  /** número, valoración: rango. Escala: de 0 o 1 a entre 3 y 10 (`rangoDeEscala`). */
  minimo?: null | number
  maximo?: null | number
  /** escala: lo que se lee en cada extremo («Nada probable», «Muy probable»). */
  etiquetaMinima?: null | string
  etiquetaMaxima?: null | string
  /** lugar: los países que se admiten (ISO 3166-1; vacío = todos los de la plataforma; uno solo = no se pregunta) y hasta qué nivel. */
  paises?: null | string[]
  nivel?: NivelDeLugar | null
  /** archivo: qué se admite (extensiones, sin punto) y cuántos. */
  extensiones?: null | string[]
  maximoDeArchivos?: null | number
  /** oculto: de dónde sale (§9). */
  origen?: 'pagina' | 'referente' | 'utm_campaign' | 'utm_content' | 'utm_medium' | 'utm_source' | 'utm_term' | 'valor-fijo' | null
  valorFijo?: null | string
  /** calculado: una fórmula sobre otros campos numéricos. */
  formula?: null | string
  /** repetidor: los campos que se repiten. */
  campos?: null | Campo[]
  maximoDeFilas?: null | number
  /** informativo: el texto que se enseña. */
  texto?: null | string
  /** consentimiento: lo que el visitante acepta, con su enlace. */
  textoDelConsentimiento?: null | string
  /** pago: un importe fijo (en la unidad de la moneda del sitio) o el de otro campo numérico o calculado. */
  modoDeImporte?: 'campo' | 'fijo' | null
  importe?: null | number
  campoDeImporte?: null | string
  descripcionDelCobro?: null | string
}

/** El campo de pago de un formulario, si lo tiene. Uno como mucho: un formulario cobra una cosa. */
export const campoDePago = (campos: Campo[]): Campo | undefined => {
  for (const campo of recorrer(campos)) if (campo.tipo === 'pago') return campo
  return undefined
}

/**
 * Cuánto cobra un formulario con estas respuestas, en la unidad de la moneda
 * (39.9), o `null` si no hay nada que cobrar. Se calcula **en el servidor** con los
 * valores ya validados; el que mande el navegador no se cree (§9, §23).
 */
export const importeDelPago = (campo: Campo, valores: Record<string, unknown>): null | number => {
  if (campo.tipo !== 'pago') return null
  const bruto = campo.modoDeImporte === 'campo' && campo.campoDeImporte ? valores[campo.campoDeImporte] : campo.importe
  const n = typeof bruto === 'number' ? bruto : Number(String(bruto ?? '').replace(',', '.'))
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.round(n * 100) / 100
}

export type Formulario = {
  id: number | string
  nombre: string
  slug: string
  campos: Campo[]
  /** Programación (§9). */
  abreEn?: null | string
  cierraEn?: null | string
  limiteDeEnvios?: null | number
  mensajeAlCerrar?: null | string
  /** Antispam (§9): el tiempo mínimo de relleno, en segundos. */
  tiempoMinimo?: null | number
  /** Cuántos envíos admite una IP en una hora. */
  limitePorIP?: null | number
  palabrasBloqueadas?: null | string[]
  captcha?: boolean | null
  /** Confirmación: mensaje en la misma página, o a dónde se lleva al visitante. */
  confirmacion?: { tipo: 'mensaje' | 'redireccion'; mensaje?: null | string; url?: null | string } | null
  guardarParcial?: boolean | null
  guardarIP?: boolean | null
  estado?: null | string
}

const SIN_ACENTOS = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** Recorre los campos, entrando en los repetidores. */
export const recorrer = function* (campos: Campo[]): Generator<Campo> {
  for (const campo of campos) {
    yield campo
    if (campo.tipo === 'repetidor' && campo.campos) yield* recorrer(campo.campos)
  }
}

export const campoPorClave = (campos: Campo[], clave: string): Campo | undefined => {
  for (const campo of recorrer(campos)) if (campo.clave === clave) return campo
  return undefined
}

/** En cuántos pasos se divide el formulario: cada `paso` abre uno nuevo. */
export const pasosDe = (campos: Campo[]): Campo[][] => {
  const pasos: Campo[][] = [[]]
  for (const campo of campos) {
    if (campo.tipo === 'paso') pasos.push([])
    else pasos[pasos.length - 1]!.push(campo)
  }
  return pasos.filter((paso, i) => paso.length > 0 || i === 0)
}

// --- Lógica condicional (§9) -------------------------------------------------------

/**
 * Cómo se lee una respuesta en una condición: una lista, separada por comas; un lugar, por su
 * camino («Perú › Lima › Lima › Miraflores») o lo que escribió el visitante.
 */
const comoTexto = (valor: unknown): string => {
  if (valor === null || valor === undefined) return ''
  if (Array.isArray(valor)) return valor.join(', ')
  if (esValorDeLugar(valor)) return String(valor.camino || valor.texto || valor.codigo || valor.pais || '')
  if (typeof valor === 'object') return ''
  return String(valor)
}
/**
 * Una respuesta vacía no es un número: «menor que 7» no se cumple mientras no se haya elegido
 * nada. Si contara como 0, la pregunta de después de una escala («¿qué podemos mejorar?») se
 * enseñaría —y se exigiría— antes de que el visitante contestara.
 */
const comoNumero = (valor: unknown): null | number => {
  const texto = comoTexto(valor).trim()
  if (texto === '') return null
  const n = Number(texto.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/**
 * «Es igual a»: en una lista, si está entre lo elegido; en un lugar, si es su país (`PE`), su
 * código oficial (`150122`) o el nombre del lugar elegido (`Miraflores`). «Contiene» busca en
 * todo su camino, así que «contiene Lima» se cumple con cualquier lugar de Lima.
 */
const esIgual = (valor: unknown, esperado: string): boolean => {
  if (Array.isArray(valor)) return valor.map((v) => SIN_ACENTOS(comoTexto(v))).includes(esperado)
  if (esValorDeLugar(valor)) {
    const elegido = String(valor.camino ?? '').split('›').pop() ?? ''
    return esperado !== '' && [valor.pais, valor.codigo, elegido, valor.texto].some((v) => typeof v === 'string' && SIN_ACENTOS(v.trim()) === esperado)
  }
  return SIN_ACENTOS(comoTexto(valor).trim()) === esperado
}

export const cumpleRegla = (regla: Regla, valores: Record<string, unknown>): boolean => {
  const valor = Object.prototype.hasOwnProperty.call(valores, regla.campo) ? valores[regla.campo] : undefined
  const texto = SIN_ACENTOS(comoTexto(valor).trim())
  const esperado = SIN_ACENTOS(comoTexto(regla.valor).trim())
  switch (regla.operador) {
    case 'igual':
      return esIgual(valor, esperado)
    case 'distinto':
      return !esIgual(valor, esperado)
    case 'contiene':
      return texto.includes(esperado)
    case 'rellenado':
      return texto !== ''
    case 'vacio':
      return texto === ''
    case 'mayor': {
      const a = comoNumero(valor)
      const b = comoNumero(regla.valor)
      return a !== null && b !== null && a > b
    }
    case 'menor': {
      const a = comoNumero(valor)
      const b = comoNumero(regla.valor)
      return a !== null && b !== null && a < b
    }
    default:
      return true
  }
}

/** Si el campo se enseña con estos valores. Sin condición, siempre. */
export const seEnseña = (campo: Campo, valores: Record<string, unknown>): boolean => {
  const condicion = campo.condicion
  if (!condicion || !condicion.reglas || condicion.reglas.length === 0) return true
  const reglas = condicion.reglas.filter((r) => r.campo)
  if (reglas.length === 0) return true
  return condicion.modo === 'alguna' ? reglas.some((r) => cumpleRegla(r, valores)) : reglas.every((r) => cumpleRegla(r, valores))
}

/** Los campos que de verdad se piden con estos valores: los visibles, sin los que no recogen nada. */
export const camposVisibles = (campos: Campo[], valores: Record<string, unknown>): Campo[] => campos.filter((campo) => !SIN_VALOR.includes(campo.tipo) && seEnseña(campo, valores))

// --- Cálculos (§9) -----------------------------------------------------------------

/**
 * Una fórmula es una suma con `+ - * / ( )` sobre números y claves de otros campos:
 * `cantidad * precio + envio`. Se evalúa **sin ejecutar código**, con un analizador
 * propio: una fórmula la escribe el cliente en su panel, y `eval` sobre eso sería
 * ejecución de código arbitraria en el servidor (§11).
 */
export const calcular = (formula: string, valores: Record<string, unknown>): null | number => {
  const tokens = formula.match(/\d+(?:[.,]\d+)?|[A-Za-z_][\w-]*|[()+\-*/]/g)
  if (!tokens) return null
  let i = 0
  const siguiente = () => tokens[i]
  const comer = () => tokens[i++]

  const factor = (): null | number => {
    const t = comer()
    if (t === undefined) return null
    if (t === '(') {
      const dentro = expresion()
      if (comer() !== ')') return null
      return dentro
    }
    if (t === '-') {
      const n = factor()
      return n === null ? null : -n
    }
    if (/^\d/.test(t)) return Number(t.replace(',', '.'))
    if (/^[A-Za-z_]/.test(t)) {
      // Solo claves propias: `__proto__` y compañía no son campos, y leerlos daría el prototipo.
      if (t === '__proto__' || t === 'constructor' || t === 'prototype') return null
      return Object.prototype.hasOwnProperty.call(valores, t) ? (comoNumero(valores[t]) ?? 0) : 0
    }
    return null
  }
  const termino = (): null | number => {
    let izquierda = factor()
    while (izquierda !== null && (siguiente() === '*' || siguiente() === '/')) {
      const operador = comer()
      const derecha = factor()
      if (derecha === null) return null
      izquierda = operador === '*' ? izquierda * derecha : derecha === 0 ? 0 : izquierda / derecha
    }
    return izquierda
  }
  const expresion = (): null | number => {
    let izquierda = termino()
    while (izquierda !== null && (siguiente() === '+' || siguiente() === '-')) {
      const operador = comer()
      const derecha = termino()
      if (derecha === null) return null
      izquierda = operador === '+' ? izquierda + derecha : izquierda - derecha
    }
    return izquierda
  }
  const resultado = expresion()
  if (resultado === null || i !== tokens.length || !Number.isFinite(resultado)) return null
  return Math.round(resultado * 100) / 100
}

/** Rellena los campos calculados a partir de los demás. Se hace en el servidor: lo que mande el navegador no se cree. */
export const conCalculados = (campos: Campo[], valores: Record<string, unknown>): Record<string, unknown> => {
  const salida = { ...valores }
  // Dos pasadas: una fórmula puede usar el resultado de otra.
  for (let vuelta = 0; vuelta < 2; vuelta++) {
    for (const campo of recorrer(campos)) {
      if (campo.tipo !== 'calculado' || !campo.formula) continue
      const valor = calcular(campo.formula, salida)
      salida[campo.clave] = valor
    }
  }
  return salida
}
